import { splitLines } from '../detect.js';
const SUPPORTED_BY_PARSER = ['pie', 'architecture', 'radar'];
/** Lightweight parser/visualiser for diagrams that don't have a grammar yet. */
export const parseGeneric = (source, type) => {
    const lines = splitLines(source);
    const nodes = [];
    const edges = [];
    if (type === 'pie' || type === 'pieChart') {
        // pie title My Pie : "Cat" : 42 ; "Dog" : 33
        let title;
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^pie\b/i.test(line))
                continue;
            const titleMatch = /^title\s+(.+)$/i.exec(line);
            if (titleMatch) {
                title = titleMatch[1]?.trim();
                continue;
            }
            const m = /^\s*"([^"]+)"\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(line);
            if (m) {
                nodes.push({
                    id: `pie_${idx++}`,
                    label: `${m[1]} (${m[2]})`,
                    shape: 'rect',
                    meta: { value: Number(m[2]), category: m[1] },
                });
            }
        }
        return { type, title, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'radar' || type === 'radar-beta') {
        // axis a, b, c
        // curve foo { a, b, c }
        const axes = [];
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^radar/i.test(line))
                continue;
            const axisMatch = /^axis\s+(.+)$/i.exec(line);
            if (axisMatch) {
                axes.push(...axisMatch[1].split(',').map((s) => s.trim()));
                continue;
            }
            const curveMatch = /^curve\s+(\S+)\s*\{([^}]*)\}/i.exec(line);
            if (curveMatch) {
                const id = `curve_${idx++}`;
                nodes.push({ id, label: curveMatch[1], shape: 'rect', meta: { axes, values: curveMatch[2] } });
                continue;
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: { axes } };
    }
    if (type === 'quadrant' || type === 'quadrantChart') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^quadrantChart/i.test(line))
                continue;
            const m = /^([^\s:]+)\s*:\s*\[[^\]]*\]\s*\[(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\]/.exec(line);
            if (m) {
                nodes.push({
                    id: `q_${idx++}`,
                    label: m[1],
                    shape: 'ellipse',
                    meta: { x: Number(m[2]), y: Number(m[3]) },
                });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'block' || type === 'block-beta' || type === 'treemap') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^block/i.test(line) || /^treemap/i.test(line))
                continue;
            const m = /^\s*(\S+)(?:\s+"([^"]*)")?(?:\s+"([^"]*)")?\s*$/.exec(line);
            if (m) {
                nodes.push({ id: `bk_${idx++}`, label: m[2] ?? m[1], shape: 'rect' });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'architecture' || type === 'architecture-beta') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^architecture/i.test(line))
                continue;
            const svc = /^service\s+(\S+)(?:\s*\(([^)]+)\))?/i.exec(line);
            if (svc) {
                nodes.push({ id: `svc_${idx++}`, label: svc[1], shape: 'rect', meta: { kind: svc[2] ?? 'service' } });
                continue;
            }
            const grp = /^group\s+(\S+)\s*\(([^)]+)\)/i.exec(line);
            if (grp) {
                nodes.push({ id: `grp_${idx++}`, label: grp[1], shape: 'rect', meta: { kind: 'group', icon: grp[2] } });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'packet' || type === 'packet-beta') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^packet/i.test(line))
                continue;
            const m = /^(\d+)(?:-(\d+))?\s*:\s*"([^"]+)"/.exec(line);
            if (m) {
                nodes.push({
                    id: `bit_${idx++}`,
                    label: m[3],
                    shape: 'rect',
                    meta: { start: Number(m[1]), end: m[2] ? Number(m[2]) : Number(m[1]) },
                });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'timeline') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^timeline\b/i.test(line))
                continue;
            const m = /^(\d{4}(?:-\d{2}(?:-\d{2})?)?)\s*:\s*(.+)$/.exec(line);
            if (m) {
                nodes.push({ id: `tl_${idx++}`, label: `${m[1]}\n${m[2]}`, shape: 'rect', meta: { time: m[1] } });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'gitgraph' || type === 'gitGraph') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^gitGraph/i.test(line))
                continue;
            const commit = /^commit(?:\s+id:\s*"([^"]+)")?(?:\s+type:\s*(\w+))?/i.exec(line);
            if (commit) {
                nodes.push({
                    id: `c_${idx++}`,
                    label: commit[1] ?? `commit ${idx}`,
                    shape: 'ellipse',
                    meta: { type: commit[2] ?? 'normal' },
                });
            }
        }
        return { type, nodes, edges, groups: [], styles: {}, meta: {} };
    }
    if (type === 'sankey' || type === 'sankey-beta') {
        let idx = 0;
        for (const raw of lines) {
            const line = raw.trim();
            if (!line || /^sankey/i.test(line))
                continue;
            const m = /^\s*(\S+)\s*,\s*(\S+)\s*,\s*(\d+(?:\.\d+)?)\s*$/.exec(line);
            if (m) {
                edges.push({
                    id: `sk_${idx++}`,
                    from: m[1],
                    to: m[2],
                    meta: { value: Number(m[3]) },
                });
            }
        }
        const nodeMap = new Map();
        for (const e of edges) {
            if (!nodeMap.has(e.from))
                nodeMap.set(e.from, { id: e.from, label: e.from, shape: 'rect' });
            if (!nodeMap.has(e.to))
                nodeMap.set(e.to, { id: e.to, label: e.to, shape: 'rect' });
        }
        return { type, nodes: Array.from(nodeMap.values()), edges, groups: [], styles: {}, meta: {} };
    }
    // Fallback: nothing parsed
    return { type, nodes: [], edges, groups: [], styles: {}, meta: {} };
};
export { SUPPORTED_BY_PARSER };
//# sourceMappingURL=generic.js.map